import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { InputRef, PasswordProps, TextAreaProps } from "antd/es/input";
import React, { CSSProperties, FC, RefAttributes, useMemo, useState } from "react";

type XmInputPasswordTextAreaProps = TextAreaProps & { textarea: true; };
type XmInputPasswordFieldProps = PasswordProps & RefAttributes<InputRef> & { textarea?: false; };
type XmInputPasswordProps = XmInputPasswordTextAreaProps | XmInputPasswordFieldProps;

const VISIBILITY_TOGGLE_STYLE: CSSProperties = {
  position: 'absolute',
  right: '8px',
  top: '2px',
  cursor: 'pointer',
  color: 'rgba(0, 0, 0, 0.45)',
  zIndex: 1,
};

/**
 * Resettable password field, when API supports: NULL = keep previous value
 * @param props
 * @returns
 */
export const XmInputPassword: FC<XmInputPasswordProps> = React.forwardRef((props, ref) => {
  const [readonly, setReadonly] = useState(true);
  const [tokenVisible, setTokenVisible] = useState(false);

  const isKeepValue = useMemo(() => {
    return props.value === null && !props["aria-required"];
  }, [props.value, props["aria-required"]]);

  if (props.textarea === true) {
    const { textarea: _, ...restProps } = props;

    return (
      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
        <Input.TextArea
          {...restProps}
          placeholder={restProps.placeholder}
          style={{
            resize: 'none',
            paddingRight: '30px',
            ...(tokenVisible ? {} : { WebkitTextSecurity: 'disc' } as CSSProperties),
          }}
          title={isKeepValue ? "Do not change this field's value to keep current password." : restProps.title}
        />
        <span
          style={VISIBILITY_TOGGLE_STYLE}
          onClick={() => setTokenVisible(!tokenVisible)}
        >
          {tokenVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </span>
      </div>
    );
  }

  const { textarea: _, ...restProps } = props;

  return (
    <Input.Password
      ref={ref}
      autoComplete='new-password'
      readOnly={readonly}
      onFocus={() => setReadonly(false)}
      title={isKeepValue ? "Do not change this field's value to keep current password." : restProps.title}
      {...restProps}
      onChange={e => {
        if (e.target.value === '') {
          restProps.onChange?.({ ...e, target: { ...e.target, value: null } });
        } else {
          restProps.onChange?.(e);
        }
      }}
      placeholder={isKeepValue ? INPUT_PLACEHOLDER : restProps.placeholder}
    />
  );
});

export const INPUT_PLACEHOLDER = "Keep current by not modifying" as const;